/* eslint-disable @stylistic/max-len */
import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy,
  OnInit, Output, ViewChild, ViewEncapsulation
} from '@angular/core';
import { GoogleMap } from '@angular/google-maps';

import MarkerClusterer from 'node-js-marker-clusterer';
import { Subscription } from 'rxjs';

import { DeviceService, StoreTypeEnum } from '@seb/web-design-system/core';

@Component({
  selector: 'sd-google-map',
  templateUrl: './google-map.component.html',
  styleUrls: ['./google-map.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [GoogleMap]
})
export class SdGoogleMapComponent implements AfterViewInit, OnInit, OnDestroy {
  constructor(
    private deviceService: DeviceService,
    private cdRef: ChangeDetectorRef,
    protected elRef: ElementRef,
  ) {}

  isMobile$ = false;
  mobileSubscription: Subscription;
  animations = google.maps.Animation;
  _zoom = 7;
  _markers = [];
  _defaultSvgPath =
    'M16,2C9.9,2,5,7.2,5,13.7c0,3.9,2,7.8,5.2,11.4c1.1,1.2,2.3,2.4,3.5,3.4c0.2,0.2,0.4,0.3,0.6,0.5l0.6,0.4l0.5,0.3c0.4,0.3,0.9,0.3,1.4,0c0,0,0.1-0.1,0.2-0.1l0.5-0.4c0.3-0.2,0.6-0.5,0.9-0.7c1.2-1,2.4-2.1,3.5-3.4C25,21.5,27,17.7,27,13.7C27,7.2,22.1,2,16,2z M16,17.6c-2.6,0-4.7-2.1-4.7-4.7c0-2.6,2.1-4.7,4.7-4.7s4.7,2.1,4.7,4.7C20.7,15.5,18.6,17.6,16,17.6z';
  _defaultAnchorPoint = new google.maps.Point(16, 32);
  _defaultIconOpacity = 1;
  _currentColor = '#363636';
  _brandColor = '#DD2D1F';
  _clusterOptions = {
    clusterColors: ['blue', 'red', 'yellow'],
    svgClusterIcon:
      '<circle cx="120" cy="120" opacity=".6" r="70" /><circle cx="120" cy="120" opacity=".4" r="95" /><circle cx="120" cy="120" opacity=".2" r="120" />',
    height: 30,
    width: 30,
    maxZoom: 6
  };
  zoomClicked = 14;
  _markerCluster;

  @Input()
  hasOffset = false;

  @Input()
  get brandColor(): string {
    return this._brandColor;
  }
  set brandColor(brandColor: string) {
    this._brandColor = brandColor;
    if (this._markerCluster) {
      this._markerCluster.clearMarkers();
      this.updateMarkerColors();
      this._markerCluster.addMarkers(this.markers);
      this._markerCluster.repaint();
    }
  }

  @Input()
  height: string;

  @Input()
  width: string;

  @Input()
  get zoom(): number {
    return this._zoom;
  }
  set zoom(zoom: number) {
    this._zoom = zoom;
  }

  @Input()
  center: google.maps.LatLngLiteral | google.maps.LatLng;

  @Input()
  options: google.maps.MapOptions = {
    fullscreenControl: false,
    streetViewControl: false,
    mapTypeControl: false
  };

  @Input()
  get markers(): any {
    return this._markers;
  }
  set markers(markers) {
    this._markers = markers.map((el) => {
      const markerConfig = { position: el.position };
      if (el.url) {
        markerConfig['icon'] = {
          url: el.url,
          anchor: el.anchorPoint ? el.anchorPoint : this._defaultAnchorPoint
        };
      } else {
        markerConfig['icon'] = {
          path: el.iconSvgPath ? el.iconSvgPath : this._defaultSvgPath,
          fillColor: el.color
            ? el.color
            : el.type.toLowerCase() === StoreTypeEnum.store
              ? this.brandColor
              : this._currentColor,
          fillOpacity: el.iconOpacity
            ? el.iconOpacity
            : this._defaultIconOpacity,
          anchor: el.anchorPoint ? el.anchorPoint : this._defaultAnchorPoint,
          strokeWeight: 0,
          scale: 1
        };
      }
      const marker = new google.maps.Marker(markerConfig);
      marker.addListener('click', () => {
        this.markerClick.emit(el.id);
        this.centerMap(marker.getPosition(), this._zoom);
      });
      return marker;
    });
    if (this._markerCluster) {
      this._markerCluster.clearMarkers();
      this._markerCluster.addMarkers(this.markers);
    }
  }

  @Input()
  get clusterOptions(): any {
    return this._clusterOptions;
  }
  set clusterOptions(clusterOptions: any) {
    this._clusterOptions = clusterOptions;
    this.updateClusterer();
  }

  @ViewChild('googleMap', { static: false })
  public gmap: GoogleMap;

  @Output()
  markerClick = new EventEmitter<string>();

  ngOnInit() {
    this.mobileSubscription = this.deviceService.isMobile.subscribe((x) => {
      this.isMobile$ = x;
      this.cdRef.markForCheck();
    });
  }

  ngAfterViewInit() {
    this.gmap.googleMap.addListener('renderingtype_changed', () => {
      this.adjustBoundaries();
    });
  }

  ngOnDestroy() {
    this.mobileSubscription?.unsubscribe();
  }

  clusterIcon = (color) =>
    window.btoa(
      `
    <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
      ` +
        this.clusterOptions.svgClusterIcon +
        `
    </svg>`,
    );

  idle() {
    if (!this._markerCluster) {
      this._markerCluster = new MarkerClusterer(
        this.gmap.googleMap,
        this.markers,
      );
      this._markerCluster.setStyles(
        this.clusterOptions.clusterColors.map((color) => ({
          url: `data:image/svg+xml;base64,${this.clusterIcon(color)}`,
          height: this.clusterOptions.height,
          width: this.clusterOptions.width,
          textColor: 'white'
        })),
      );
      this._markerCluster.setMaxZoom(this.clusterOptions.maxZoom);
    }
  }

  updateMarkerColors() {
    this.markers.map((el) => {
      el.icon.fillColor =
        el.icon.fillColor !== this._currentColor
          ? this.brandColor
          : this._currentColor;
    });
  }

  updateClusterer() {
    if (this._markerCluster) {
      this._markerCluster.setStyles(
        this.clusterOptions.clusterColors.map((color) => ({
          url: `data:image/svg+xml;base64,${this.clusterIcon(color)}`,
          height: this.clusterOptions.height,
          width: this.clusterOptions.width,
          textColor: 'white'
        })),
      );
      this._markerCluster.setMaxZoom(this.clusterOptions.maxZoom);
      this._markerCluster.repaint();
    }
  }

  setBounds(
    bounds: google.maps.LatLngBoundsLiteral | google.maps.LatLngBounds,
    zoom?: number,
  ) {
    this.gmap.fitBounds(bounds);
    this.gmap.panToBounds(bounds);
    this.zoom = zoom ? zoom : this.gmap.getZoom();
    if (!this.isMobile$ && this.hasOffset) {
      setTimeout(() => {
        this.gmap.panBy(-200, 0);
      }, 200);
    }
  }

  resetAllMarkers(excludedMarker?) {
    this.markers.map((marker) => {
      if (marker !== excludedMarker) {
        marker.setAnimation(null);
        this.setMarkerScale(marker, 1);
      }
    });
  }

  setSelectMarker(marker) {
    this.resetAllMarkers(marker);
    marker.setAnimation(this.animations.BOUNCE);
    this.setMarkerScale(marker, 1.5);
  }

  centerMap(coords, zoom) {
    this.center = coords;
    this.zoom = !!zoom && zoom > 12 ? zoom : 12;
    if (!this.isMobile$ && this.hasOffset) {
      setTimeout(() => {
        this.gmap.panBy(-200, 0);
      });
    }
  }

  getBounds() {
    return this.gmap.getBounds();
  }

  setMarkerScale(marker, scale: number) {
    const iconCopy = marker.getIcon();
    iconCopy['scale'] = scale;
    marker.setIcon(iconCopy);
  }

  adjustBoundaries() {
    const bounds = this.gmap?.getBounds();
    const mapZoom = this.getBoundsZoomLevel(bounds, {
      height: this.elRef.nativeElement.offsetHeight,
      width: this.elRef.nativeElement.offsetWidth
    });
    this.setBounds(bounds, mapZoom + 2);
  }

  getBoundsZoomLevel(bounds, mapDim) {
    const WORLD_DIM = { height: 256, width: 256 };
    const ZOOM_MAX = 21;

    function latRad(lat) {
      const sin = Math.sin((lat * Math.PI) / 180);
      const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
      return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    }

    function zoom(mapPx, worldPx, fraction) {
      return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

    const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  }
}
